import { isPromise } from "radash";

class UnknownError extends Error {
  constructor(cause: unknown) {
    const message = isError(cause)
      ? cause.message
      : `Unknown error: ${String(cause)}`;
    super(message);
    this.name = "UnknownError";
    this.cause = cause;
  }
}

function toUnknownError(error: unknown): UnknownError {
  return error instanceof UnknownError ? error : new UnknownError(error);
}

function isError(error: unknown): error is Error {
  return error instanceof Error;
}

/**
 * Makes sure an error is an actual error object
 */
function ensureError(error: unknown): Error {
  return isError(error) ? error : toUnknownError(error);
}

/**
 * Returns a thrown Error
 * @param callback
 */
function returnError<Args extends unknown[], Return>(
  callback: (...args: Args) => Promise<Return>,
): (...args: Args) => Promise<Error | Return>;
function returnError<Args extends unknown[], Return>(
  callback: (...args: Args) => Return,
): (...args: Args) => Error | Return;
function returnError(callback: (...args: unknown[]) => unknown) {
  return (...args: unknown[]) => {
    try {
      const result = callback(...args);
      return isPromise(result) ? result.catch(ensureError) : result;
    } catch (error) {
      return ensureError(error);
    }
  };
}

/**
 * Rethrows a returned Error
 */
function throwError<Args extends unknown[], Return>(
  callback: (...args: Args) => Promise<Return>,
): (...args: Args) => Promise<Exclude<Return, Error>>;
function throwError<Args extends unknown[], Return>(
  callback: (...args: Args) => Return,
): (...args: Args) => Exclude<Return, Error>;
function throwError(callback: (...args: unknown[]) => unknown) {
  function handleResult(result: unknown) {
    if (isError(result)) {
      throw result;
    }
    return result;
  }

  return (...args: unknown[]) => {
    const result = callback(...args);
    return isPromise(result) ? result.then(handleResult) : handleResult(result);
  };
}

export { isError, returnError, throwError, toUnknownError, UnknownError };
